// Dashboard — one screen answering "how is the machine doing today?"
import React, { useState, useEffect, useCallback } from 'react';
import {
  Database, Send, MessageSquare, Flame, MonitorPlay, HeartPulse,
  TrendingUp, RefreshCw, AlertTriangle,
} from 'lucide-react';
import { apiService } from '../services/apiService';
import { Card, StatCard, Chip, Btn, Spinner, fmtNum } from './ui';

const Sparkline = ({ points, width = 150, height = 36 }) => {
  if (!points || points.length < 2) return null;
  const max = Math.max(...points.map((p) => p.sent), 1);
  const step = width / (points.length - 1);
  const coords = points.map((p, i) => `${(i * step).toFixed(1)},${(height - 4 - ((p.sent / max) * (height - 8))).toFixed(1)}`);
  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      <polyline
        points={coords.join(' ')}
        fill="none" stroke="var(--c-primary)" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round"
      />
    </svg>
  );
};

const STAGE_COLORS = {
  new: 'var(--c-blue)', contacted: 'var(--c-text-3)', interested: 'var(--c-yellow)',
  qualified: 'var(--c-purple)', won: 'var(--c-primary)', lost: 'var(--c-red)',
};

const Dashboard = ({ onNavigate }) => {
  const [summary, setSummary] = useState(null);
  const [cockpit, setCockpit] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.allSettled([
        apiService.getDashboardSummary(),
        apiService.getCockpit(),
      ]);
      if (s.status === 'fulfilled' && !s.value.error) setSummary(s.value);
      if (c.status === 'fulfilled' && !c.value.error) setCockpit(c.value);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 120000);
    return () => clearInterval(t);
  }, [load]);

  if (loading && !summary) {
    return <div style={{ padding: 80, textAlign: 'center' }}><Spinner size={26} /></div>;
  }
  if (!summary) {
    return <div style={{ padding: 60, textAlign: 'center', color: 'var(--c-text-2)' }}>Could not load the dashboard. <Btn small onClick={load} style={{ marginLeft: 8 }}>Retry</Btn></div>;
  }

  const s = summary;
  const today = cockpit?.today || {};
  const pipelineEntries = Object.entries(s.pipeline || {}).sort((a, b) => b[1] - a[1]);
  const pipelineTotal = pipelineEntries.reduce((acc, [, n]) => acc + n, 0) || 1;
  const mb = s.mailboxes || {};

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto', padding: '24px 20px 60px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 22, fontWeight: 700, color: 'var(--c-text)' }}>Today</div>
          <div style={{ fontSize: 13, color: 'var(--c-text-2)', marginTop: 3 }}>
            {new Date().toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'long' })}
          </div>
        </div>
        <Btn small onClick={load}><RefreshCw size={13} /> Refresh</Btn>
      </div>

      {/* Stat tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 14 }}>
        <StatCard
          icon={Database} label="Records"
          value={fmtNum(s.profiles?.total)}
          sub={`+${fmtNum(s.profiles?.today)} scraped today`}
          onClick={() => onNavigate('records')}
        />
        <StatCard
          icon={Send} label="Emails sent today"
          value={fmtNum(s.sends_today?.total)}
          sub={`${(s.sends_today?.by_mailbox || []).length} mailboxes active`}
          color="var(--c-blue)"
          onClick={() => onNavigate('email')}
        />
        <StatCard
          icon={MessageSquare} label="Replies today"
          value={fmtNum(s.replies_today)}
          sub={today.unread_replies != null ? `${today.unread_replies} unread` : undefined}
          color="var(--c-purple)"
          onClick={() => onNavigate('inbox')}
        />
        <StatCard
          icon={Flame} label="Hot leads"
          value={fmtNum(s.hot_leads)}
          sub={today.followups_due != null ? `${today.followups_due} follow-ups due` : undefined}
          color="var(--c-yellow)"
          onClick={() => onNavigate('pipeline')}
        />
        <StatCard
          icon={MonitorPlay} label="Demos (7 days)"
          value={`${fmtNum(s.demos_7d?.delivered)}`}
          sub={`${s.demos_7d?.opened || 0} opened · ${s.demos_7d?.total_opens || 0} total opens`}
          color="var(--c-accent)"
          onClick={() => onNavigate('demos')}
        />
        <StatCard
          icon={HeartPulse} label="Mailboxes"
          value={`${mb.healthy || 0}/${mb.total || 0}`}
          sub={mb.paused ? `${mb.paused} paused` : 'all monitored hourly'}
          color={(mb.errors || []).length ? 'var(--c-red)' : 'var(--c-primary)'}
          onClick={() => onNavigate('mailboxes')}
        />
      </div>

      {/* Mailbox errors banner */}
      {(mb.errors || []).length > 0 && (
        <Card style={{ marginTop: 14, borderColor: 'rgba(239,68,68,0.4)', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <AlertTriangle size={18} style={{ color: 'var(--c-red)', flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: 13, color: 'var(--c-text)' }}>
            <strong>Mailbox problems:</strong>{' '}
            {mb.errors.map((e) => `${e.email} (${e.error})`).join(' · ')}
          </div>
        </Card>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14, marginTop: 14 }}>
        {/* Send trend */}
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--c-text)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <TrendingUp size={15} style={{ color: 'var(--c-primary)' }} /> Outreach, last 7 days
            </div>
            <div style={{ fontSize: 12, color: 'var(--c-text-3)' }}>
              {fmtNum((s.send_trend_7d || []).reduce((a, d) => a + d.sent, 0))} sent
            </div>
          </div>
          <Sparkline points={s.send_trend_7d} width={280} height={54} />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, color: 'var(--c-text-3)', marginTop: 6 }}>
            {(s.send_trend_7d || []).map((d) => (
              <span key={d.date}>{d.date.slice(5).replace('-', '/')}</span>
            ))}
          </div>
        </Card>

        {/* Pipeline breakdown */}
        <Card onClick={() => onNavigate('pipeline')}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--c-text)', marginBottom: 14 }}>Pipeline</div>
          <div style={{ display: 'flex', height: 10, borderRadius: 6, overflow: 'hidden', marginBottom: 12 }}>
            {pipelineEntries.map(([stage, n]) => (
              <div key={stage} style={{ width: `${(n / pipelineTotal) * 100}%`, background: STAGE_COLORS[stage] || 'var(--c-text-3)', minWidth: n > 0 ? 3 : 0 }} title={`${stage}: ${n}`} />
            ))}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {pipelineEntries.map(([stage, n]) => (
              <span key={stage} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--c-text-2)' }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: STAGE_COLORS[stage] || 'var(--c-text-3)' }} />
                {stage} <strong style={{ color: 'var(--c-text)' }}>{fmtNum(n)}</strong>
              </span>
            ))}
          </div>
        </Card>

        {/* Sends per mailbox today */}
        <Card onClick={() => onNavigate('mailboxes')}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--c-text)', marginBottom: 12 }}>Sends today by mailbox</div>
          {(s.sends_today?.by_mailbox || []).length === 0 && (
            <div style={{ fontSize: 12.5, color: 'var(--c-text-3)' }}>No sends yet today — batches run 08:00 and 13:00, Mon to Fri.</div>
          )}
          {(s.sends_today?.by_mailbox || []).map((m) => (
            <div key={m.mailbox} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '5px 0', fontSize: 12.5 }}>
              <span style={{ color: 'var(--c-text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.mailbox}</span>
              <Chip tone="blue">{m.sent}</Chip>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
